<template>
  <div>
    <Nav></Nav>
    <b-overlay :show="loadingReports" rounded="sm" spinner-type="grow">
      <b-container fluid>
        <b-row>
          <b-col>
            <h1>Reporte Diario Almacén General</h1>
            <p class="text-muted">Genera un reporte PDF con órdenes de compra y manufactura filtradas por sucursal y proveedor</p>
          </b-col>
        </b-row>

        <b-card class="mt-3">
          <b-form @submit.prevent="generateReport">
            <b-row>
              <b-col md="6">
                <b-form-group label="Fecha Inicial" label-for="init-date">
                  <b-form-datepicker
                    id="init-date"
                    v-model="reportForm.init_date"
                    :max="new Date()"
                    placeholder="Seleccionar fecha inicial"
                    locale="es"
                    required
                  />
                </b-form-group>
              </b-col>
              
              <b-col md="6">
                <b-form-group label="Fecha Final" label-for="end-date">
                  <b-form-datepicker
                    id="end-date"
                    v-model="reportForm.end_date"
                    :max="new Date()"
                    :min="reportForm.init_date"
                    placeholder="Seleccionar fecha final"
                    locale="es"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col class="text-center">
                <b-button 
                  type="submit" 
                  variant="primary" 
                  :disabled="!reportForm.init_date || !reportForm.end_date || loadingReports"
                  class="mx-2"
                >
                  <b-icon icon="download" class="mr-1"></b-icon>
                  Generar PDF
                </b-button>
                <b-button 
                  variant="outline-secondary" 
                  @click="resetForm"
                  :disabled="loadingReports"
                  class="mx-2"
                >
                  <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                  Limpiar
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-card>

        <b-card class="mt-4" header="Información del Reporte">
          <b-row>
            <b-col md="4">
              <div class="info-section p-3 mb-3 border rounded bg-light">
                <h5 class="text-primary">
                  <b-icon icon="clipboard-data" class="mr-2"></b-icon>
                  Sección 1
                </h5>
                <h6>Órdenes de Compra - Sucursal 21</h6>
                <p class="small text-muted mb-0">
                  Muestra todas las órdenes de compra de la sucursal 21 en el período seleccionado
                </p>
              </div>
            </b-col>
            
            <b-col md="4">
              <div class="info-section p-3 mb-3 border rounded bg-light">
                <h5 class="text-success">
                  <b-icon icon="gear-fill" class="mr-2"></b-icon>
                  Sección 2
                </h5>
                <h6>Órdenes de Manufactura - Sucursal 21</h6>
                <p class="small text-muted mb-0">
                  Lista las órdenes de manufactura de la sucursal 21 con cantidades planificadas y producidas
                </p>
              </div>
            </b-col>
            
            <b-col md="4">
              <div class="info-section p-3 mb-3 border rounded bg-light">
                <h5 class="text-warning">
                  <b-icon icon="building" class="mr-2"></b-icon>
                  Sección 3
                </h5>
                <h6>Órdenes de Compra - Proveedor ID 1</h6>
                <p class="small text-muted mb-0">
                  Detalla todas las órdenes del proveedor principal con información de sucursal
                </p>
              </div>
            </b-col>
          </b-row>
          
          <b-alert variant="info" show class="mt-3">
            <b-icon icon="info-circle-fill" class="mr-2"></b-icon>
            <strong>Nota:</strong> Las órdenes canceladas no se incluyen en el reporte
          </b-alert>
        </b-card>
      </b-container>
    </b-overlay>
  </div>
</template>

<script>
import Nav from '@/components/Nav.vue'
import axios from 'axios'

export default {
  name: 'ReporteAlmacenGeneral',
  components: {
    Nav
  },
  data() {
    return {
      loadingReports: false,
      reportForm: {
        init_date: '',
        end_date: ''
      }
    }
  },
  mounted() {
    this.setDefaultDates()
  },
  methods: {
    setDefaultDates() {
      const today = new Date()
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
      
      this.reportForm.end_date = this.formatDate(today)
      this.reportForm.init_date = this.formatDate(firstDay)
    },
    
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    async generateReport() {
      if (!this.reportForm.init_date || !this.reportForm.end_date) {
        this.makeToast('warning', 'Advertencia', 'Por favor seleccione ambas fechas')
        return
      }
      
      if (new Date(this.reportForm.end_date) < new Date(this.reportForm.init_date)) {
        this.makeToast('danger', 'Error', 'La fecha final debe ser mayor o igual a la fecha inicial')
        return
      }
      
      this.loadingReports = true
      
      try {
        const response = await axios.post('/report/almacen/general', {}, {
          params: {
            init_date: this.reportForm.init_date,
            end_date: this.reportForm.end_date
          },
          responseType: 'blob'
        })
        
        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `reporte_almacen_general_${this.reportForm.init_date}_${this.reportForm.end_date}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.makeToast('success', 'Éxito', 'Reporte generado exitosamente')
      } catch (error) {
        console.error('Error generating report:', error)
        let errorMessage = 'Error al generar el reporte'
        if (error.response && error.response.data) {
          if (error.response.data instanceof Blob) {
            const text = await error.response.data.text()
            try {
              const errorObj = JSON.parse(text)
              errorMessage += ': ' + errorObj.error
            } catch (e) {
              errorMessage += ': Error del servidor'
            }
          } else {
            errorMessage += ': ' + (error.response.data.error || error.response.data)
          }
        }
        this.makeToast('danger', 'Error', errorMessage)
      } finally {
        this.loadingReports = false
      }
    },
    
    resetForm() {
      this.setDefaultDates()
    },
    
    makeToast(variant, title, message) {
      this.$bvToast.toast(message, {
        title: title,
        variant: variant,
        solid: true,
        autoHideDelay: 5000
      })
    }
  }
}
</script>

<style scoped>
.info-section {
  transition: transform 0.2s ease-in-out;
}

.info-section:hover {
  transform: translateY(-2px);
}

h1 {
  color: #2c3e50;
  font-weight: 600;
}

.text-muted {
  font-size: 0.95em;
}
</style>